/* ==========================================================================
   Jabones de mi Má — Sistema "Espuma" · Comportamientos globales
   Expone window.MimaFoam.init(rootEl) y auto-inicia en DOMContentLoaded.

   Atributos:
   - data-mima-bubbles (+ data-mima-bubbles-count, data-mima-bubbles-theme)
   - data-mima-reveal  (+ style="--mima-reveal-delay:.15s")
   - data-mima-tilt
   - data-mima-marquee
   - data-mima-countup

   Vanilla JS, sin dependencias. Todo el movimiento respeta
   prefers-reduced-motion y se pausa fuera de pantalla / pestaña oculta.
   ========================================================================== */
(function () {
  'use strict';

  var REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');
  var FINE_POINTER = window.matchMedia('(pointer: fine)');
  var MAX_BUBBLES = 40;
  var EASE = 'cubic-bezier(.22,.61,.36,1)';

  function reducedMotion() {
    return REDUCED.matches;
  }

  /* Guardas anti-doble-inicio por elemento y comportamiento */
  var seen = {
    bubbles: new WeakSet(),
    reveal: new WeakSet(),
    tilt: new WeakSet(),
    marquee: new WeakSet(),
    countup: new WeakSet()
  };

  /* Campos de burbujas vivos (para apagarlos si cambia prefers-reduced-motion) */
  var bubbleFields = [];

  /**
   * Llama onChange(true|false) según el elemento esté en viewport y la
   * pestaña visible. Devuelve una función destructora.
   */
  function watchActivity(el, onChange) {
    var inView = true;
    var pageVisible = document.visibilityState !== 'hidden';

    function update() {
      onChange(inView && pageVisible);
    }

    var io = null;
    if ('IntersectionObserver' in window) {
      io = new IntersectionObserver(
        function (entries) {
          inView = entries[0].isIntersecting;
          update();
        },
        { threshold: 0.01 }
      );
      io.observe(el);
    }

    function onVisibility() {
      pageVisible = document.visibilityState !== 'hidden';
      update();
    }
    document.addEventListener('visibilitychange', onVisibility);

    return function destroy() {
      if (io) io.disconnect();
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }

  /* ========================================================================
     data-mima-bubbles — motor de burbujas iridiscentes en canvas
     ======================================================================== */
  function BubbleField(host) {
    this.host = host;
    this.theme = host.getAttribute('data-mima-bubbles-theme') === 'dark' ? 'dark' : 'light';

    var count = parseInt(host.getAttribute('data-mima-bubbles-count'), 10);
    if (!isFinite(count) || count <= 0) count = 18;
    this.count = Math.min(count, MAX_BUBBLES);

    if (window.getComputedStyle(host).position === 'static') {
      host.style.position = 'relative';
    }

    var canvas = document.createElement('canvas');
    canvas.className = 'mima-bubble-canvas';
    canvas.setAttribute('aria-hidden', 'true');
    host.insertBefore(canvas, host.firstChild);
    this.canvas = canvas;
    this.ctx = canvas.getContext('2d');

    this.bubbles = [];
    this.running = false;
    this.raf = 0;
    this.last = 0;
    this.width = 0;
    this.height = 0;

    this.resize();
    this.seed(true);

    var self = this;
    if ('ResizeObserver' in window) {
      this.ro = new ResizeObserver(function () {
        self.resize();
      });
      this.ro.observe(host);
    } else {
      this.onWinResize = function () {
        self.resize();
      };
      window.addEventListener('resize', this.onWinResize);
    }

    this.destroyActivity = watchActivity(host, function (active) {
      if (active) {
        self.start();
      } else {
        self.stop();
      }
    });
  }

  BubbleField.prototype.resize = function () {
    var rect = this.host.getBoundingClientRect();
    var dpr = Math.min(window.devicePixelRatio || 1, 2);
    this.width = Math.max(rect.width, 0);
    this.height = Math.max(rect.height, 0);
    this.canvas.width = Math.round(this.width * dpr);
    this.canvas.height = Math.round(this.height * dpr);
    this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    if (!this.running) this.draw(0);
  };

  BubbleField.prototype.makeBubble = function (initial) {
    return {
      baseX: Math.random() * this.width,
      y: initial ? Math.random() * this.height : this.height + 30 + Math.random() * 50,
      r: 4 + Math.random() * 22,
      speed: 12 + Math.random() * 26,
      amp: 8 + Math.random() * 22,
      freq: 0.4 + Math.random() * 0.9,
      phase: Math.random() * Math.PI * 2,
      hue: 18 + Math.random() * 160,
      alpha: 0.35 + Math.random() * 0.4
    };
  };

  BubbleField.prototype.seed = function (initial) {
    this.bubbles = [];
    for (var i = 0; i < this.count; i++) {
      this.bubbles.push(this.makeBubble(initial));
    }
  };

  BubbleField.prototype.drawBubble = function (b, t) {
    var ctx = this.ctx;
    var dark = this.theme === 'dark';
    var wob = 1 + 0.06 * Math.sin(t * 1.6 + b.phase);
    var r = b.r * wob;
    var x = b.baseX + Math.sin(t * b.freq + b.phase) * b.amp;
    var hue = (b.hue + t * 12) % 360;
    var a = b.alpha;

    /* Cuerpo: halo iridiscente translúcido */
    var g = ctx.createRadialGradient(x - r * 0.35, b.y - r * 0.35, r * 0.1, x, b.y, r);
    if (dark) {
      g.addColorStop(0, 'rgba(255,255,255,' + (0.5 * a).toFixed(3) + ')');
      g.addColorStop(0.55, 'hsla(' + hue.toFixed(1) + ',70%,82%,' + (0.16 * a).toFixed(3) + ')');
      g.addColorStop(1, 'hsla(' + ((hue + 40) % 360).toFixed(1) + ',75%,70%,' + (0.05 * a).toFixed(3) + ')');
    } else {
      g.addColorStop(0, 'rgba(255,255,255,' + (0.75 * a).toFixed(3) + ')');
      g.addColorStop(0.55, 'hsla(' + hue.toFixed(1) + ',55%,78%,' + (0.2 * a).toFixed(3) + ')');
      g.addColorStop(1, 'hsla(' + ((hue + 40) % 360).toFixed(1) + ',60%,68%,' + (0.06 * a).toFixed(3) + ')');
    }
    ctx.beginPath();
    ctx.arc(x, b.y, r, 0, Math.PI * 2);
    ctx.fillStyle = g;
    ctx.fill();

    /* Borde sutil */
    ctx.beginPath();
    ctx.arc(x, b.y, r, 0, Math.PI * 2);
    ctx.lineWidth = 1;
    ctx.strokeStyle = dark
      ? 'hsla(' + hue.toFixed(1) + ',65%,88%,' + (0.5 * a).toFixed(3) + ')'
      : 'hsla(' + hue.toFixed(1) + ',45%,62%,' + (0.38 * a).toFixed(3) + ')';
    ctx.stroke();

    /* Brillo superior izquierdo */
    ctx.beginPath();
    ctx.arc(x - r * 0.38, b.y - r * 0.42, r * 0.16, 0, Math.PI * 2);
    ctx.fillStyle = 'rgba(255,255,255,' + ((dark ? 0.75 : 0.85) * a).toFixed(3) + ')';
    ctx.fill();
  };

  BubbleField.prototype.draw = function (t) {
    var ctx = this.ctx;
    ctx.clearRect(0, 0, this.width, this.height);
    for (var i = 0; i < this.bubbles.length; i++) {
      this.drawBubble(this.bubbles[i], t);
    }
  };

  BubbleField.prototype.tick = function (now) {
    if (!this.running) return;
    var dt = Math.min(now - this.last, 50) / 1000;
    this.last = now;
    var t = now / 1000;

    for (var i = 0; i < this.bubbles.length; i++) {
      var b = this.bubbles[i];
      b.y -= b.speed * dt;
      if (b.y < -b.r * 2.5) {
        this.bubbles[i] = this.makeBubble(false);
      }
    }
    this.draw(t);

    var self = this;
    this.raf = requestAnimationFrame(function (n) {
      self.tick(n);
    });
  };

  BubbleField.prototype.start = function () {
    if (this.running || reducedMotion() || !this.ctx) return;
    if (this.width === 0 || this.height === 0) return;
    this.running = true;
    var self = this;
    this.last = performance.now();
    this.raf = requestAnimationFrame(function (n) {
      self.tick(n);
    });
  };

  BubbleField.prototype.stop = function () {
    this.running = false;
    if (this.raf) cancelAnimationFrame(this.raf);
    this.raf = 0;
  };

  BubbleField.prototype.destroy = function () {
    this.stop();
    if (this.destroyActivity) this.destroyActivity();
    if (this.ro) this.ro.disconnect();
    if (this.onWinResize) window.removeEventListener('resize', this.onWinResize);
    if (this.canvas.parentNode) this.canvas.parentNode.removeChild(this.canvas);
  };

  function initBubbles(el) {
    if (reducedMotion()) return; /* fallback: sin movimiento, sin canvas */
    var field = new BubbleField(el);
    if (!field.ctx) {
      field.destroy();
      return;
    }
    bubbleFields.push(field);
  }

  /* ========================================================================
     data-mima-reveal — fade + subida al entrar en viewport
     ======================================================================== */
  function initReveal(el) {
    if (reducedMotion() || !('IntersectionObserver' in window)) {
      el.classList.add('mima-is-revealed');
      return;
    }
    var io = new IntersectionObserver(
      function (entries, obs) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            el.classList.add('mima-is-revealed');
            obs.disconnect();
          }
        });
      },
      { threshold: 0.15, rootMargin: '0px 0px -8% 0px' }
    );
    io.observe(el);
  }

  /* ========================================================================
     data-mima-tilt — tilt 3D sutil (solo desktop / puntero fino)
     ======================================================================== */
  function initTilt(el) {
    if (reducedMotion() || !FINE_POINTER.matches) return;

    var MAX_DEG = 4;
    var raf = 0;

    el.addEventListener('pointermove', function (ev) {
      if (ev.pointerType && ev.pointerType !== 'mouse') return;
      var rect = el.getBoundingClientRect();
      if (rect.width === 0 || rect.height === 0) return;
      var px = (ev.clientX - rect.left) / rect.width - 0.5;
      var py = (ev.clientY - rect.top) / rect.height - 0.5;
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(function () {
        el.style.transition = 'transform .18s ' + EASE;
        el.style.transform =
          'perspective(800px) rotateX(' + (-py * MAX_DEG).toFixed(2) + 'deg) rotateY(' + (px * MAX_DEG).toFixed(2) + 'deg)';
      });
    });

    el.addEventListener('pointerleave', function () {
      cancelAnimationFrame(raf);
      el.style.transition = 'transform .5s ' + EASE;
      el.style.transform = '';
    });
  }

  /* ========================================================================
     data-mima-marquee — scroll infinito suave, pausa en hover
     ======================================================================== */
  function initMarquee(el) {
    if (reducedMotion()) return; /* fallback: CSS habilita scroll nativo */

    var gap = window.getComputedStyle(el).columnGap;
    var track = document.createElement('div');
    track.className = 'mima-marquee__track';
    if (gap && gap !== 'normal' && gap !== '0px') {
      track.style.gap = gap;
    }

    while (el.firstChild) {
      track.appendChild(el.firstChild);
    }
    el.appendChild(track);

    var originals = Array.prototype.slice.call(track.children);

    function cloneSet() {
      originals.forEach(function (node) {
        var clone = node.cloneNode(true);
        clone.setAttribute('aria-hidden', 'true');
        clone.removeAttribute('id');
        var withIds = clone.querySelectorAll('[id]');
        for (var i = 0; i < withIds.length; i++) withIds[i].removeAttribute('id');
        var focusables = clone.querySelectorAll('a, button, input, select, textarea, [tabindex]');
        for (var j = 0; j < focusables.length; j++) focusables[j].setAttribute('tabindex', '-1');
        if (clone.matches && clone.matches('a, button, input, select, textarea, [tabindex]')) {
          clone.setAttribute('tabindex', '-1');
        }
        track.appendChild(clone);
      });
    }

    /* Duplicar hasta que la mitad de la pista cubra el viewport (loop sin huecos) */
    cloneSet();
    var guard = 0;
    while (track.scrollWidth / 2 < el.clientWidth && guard < 6) {
      cloneSet();
      cloneSet();
      guard++;
    }

    var period = track.scrollWidth / 2;
    var offset = 0;
    var speed = 40; /* px/s */
    var running = false;
    var paused = false;
    var raf = 0;
    var last = 0;

    function tick(now) {
      if (!running) return;
      var dt = Math.min(now - last, 50) / 1000;
      last = now;
      if (!paused) {
        offset -= speed * dt;
        if (-offset >= period) offset += period;
        track.style.transform = 'translate3d(' + offset.toFixed(2) + 'px,0,0)';
      }
      raf = requestAnimationFrame(tick);
    }

    function start() {
      if (running) return;
      running = true;
      last = performance.now();
      raf = requestAnimationFrame(tick);
    }

    function stop() {
      running = false;
      if (raf) cancelAnimationFrame(raf);
      raf = 0;
    }

    el.addEventListener('mouseenter', function () {
      paused = true;
    });
    el.addEventListener('mouseleave', function () {
      paused = false;
    });
    el.addEventListener('focusin', function () {
      paused = true;
    });
    el.addEventListener('focusout', function () {
      paused = false;
    });

    if ('ResizeObserver' in window) {
      var ro = new ResizeObserver(function () {
        period = track.scrollWidth / 2;
      });
      ro.observe(el);
      ro.observe(track);
    }

    watchActivity(el, function (active) {
      if (active) {
        start();
      } else {
        stop();
      }
    });
  }

  /* ========================================================================
     data-mima-countup — anima el número contenido al hacerse visible
     ======================================================================== */
  function parseNumText(text) {
    var m = text.match(/^([^0-9\-]*)(-?[0-9][0-9.,]*)([\s\S]*)$/);
    if (!m) return null;

    var prefix = m[1];
    var num = m[2];
    var suffix = m[3];

    var lastDot = num.lastIndexOf('.');
    var lastComma = num.lastIndexOf(',');
    var decIdx = Math.max(lastDot, lastComma);

    var decSep = null;
    var groupSep = null;
    var decimals = 0;

    if (decIdx > -1) {
      var tail = num.length - decIdx - 1;
      var hasBoth = lastDot > -1 && lastComma > -1;
      if (tail === 3 && !hasBoth && num.replace(/[.,]/g, '').length > 3) {
        /* "12.500" / "12,500" → separador de miles */
        groupSep = num.charAt(decIdx);
      } else {
        decSep = num.charAt(decIdx);
        decimals = tail;
        var other = decSep === '.' ? ',' : '.';
        if (num.indexOf(other) > -1) groupSep = other;
      }
    }

    var clean = num.replace(/[.,]/g, '');
    var value = parseInt(clean, 10) / Math.pow(10, decimals);
    if (!isFinite(value)) return null;

    return {
      value: value,
      prefix: prefix,
      suffix: suffix,
      decimals: decimals,
      groupSep: groupSep,
      decSep: decSep || '.'
    };
  }

  function formatNum(v, info) {
    var negative = v < 0;
    var s = Math.abs(v).toFixed(info.decimals);
    var parts = s.split('.');
    if (info.groupSep) {
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, info.groupSep);
    }
    return info.prefix + (negative ? '-' : '') + parts.join(info.decimals > 0 ? info.decSep : '') + info.suffix;
  }

  function initCountup(el) {
    var info = parseNumText((el.textContent || '').trim());
    if (!info || info.value === 0) return;

    var original = el.textContent;
    var DURATION = 1400;

    function setFinal() {
      el.textContent = original;
      el.classList.remove('mima-is-counting');
    }

    if (reducedMotion() || !('IntersectionObserver' in window)) {
      setFinal();
      return;
    }

    var io = new IntersectionObserver(
      function (entries, obs) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          obs.disconnect();

          if (reducedMotion()) {
            setFinal();
            return;
          }

          el.classList.add('mima-is-counting');
          var t0 = performance.now();

          function frame(now) {
            var p = Math.min((now - t0) / DURATION, 1);
            var eased = 1 - Math.pow(1 - p, 3);
            el.textContent = formatNum(info.value * eased, info);
            if (p < 1) {
              requestAnimationFrame(frame);
            } else {
              setFinal();
            }
          }
          requestAnimationFrame(frame);
        });
      },
      { threshold: 0.4 }
    );
    io.observe(el);
  }

  /* ========================================================================
     Orquestación
     ======================================================================== */
  function collect(root, attr) {
    var els = [];
    if (root.nodeType === 1 && root.hasAttribute && root.hasAttribute(attr)) {
      els.push(root);
    }
    if (root.querySelectorAll) {
      var found = root.querySelectorAll('[' + attr + ']');
      for (var i = 0; i < found.length; i++) els.push(found[i]);
    }
    return els;
  }

  function run(root, attr, set, fn) {
    collect(root, attr).forEach(function (el) {
      if (set.has(el)) return;
      set.add(el);
      try {
        fn(el);
      } catch (err) {
        if (window.console && console.error) {
          console.error('[mima-foam] Error iniciando ' + attr, err);
        }
      }
    });
  }

  function init(root) {
    var target = root && root.nodeType ? root : document.documentElement;
    document.documentElement.classList.add('mima-foam-ready');
    run(target, 'data-mima-bubbles', seen.bubbles, initBubbles);
    run(target, 'data-mima-reveal', seen.reveal, initReveal);
    run(target, 'data-mima-tilt', seen.tilt, initTilt);
    run(target, 'data-mima-marquee', seen.marquee, initMarquee);
    run(target, 'data-mima-countup', seen.countup, initCountup);
  }

  /* Si el usuario activa movimiento reducido en caliente: apagar burbujas */
  function onReducedChange() {
    if (!REDUCED.matches) return;
    while (bubbleFields.length) {
      bubbleFields.pop().destroy();
    }
  }
  if (REDUCED.addEventListener) {
    REDUCED.addEventListener('change', onReducedChange);
  } else if (REDUCED.addListener) {
    REDUCED.addListener(onReducedChange);
  }

  window.MimaFoam = {
    version: '1.0.0',
    init: init
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init(document.documentElement);
    });
  } else {
    init(document.documentElement);
  }

  /* Editor de temas de Shopify: re-iniciar al recargar secciones */
  document.addEventListener('shopify:section:load', function (event) {
    if (event.target) init(event.target);
  });
})();
